<template>
  <div class="container">
    <h1 class="sr-only">{{ $t('learn_page.hero_title') }}</h1>
    <!-- Content Modal (for free videos and accidents) -->
    <ContentModal
      :content="contentChosen"
      :isVideoModal.sync="isContentVideoModal"
      :isAccidentModal.sync="isAccidentModal"
    />

    <!-- Video Modal (with payment/subscription logic) -->
    <b-modal
      v-model="isVideoModal"
      content-class="airspirit-modal"
      hide-footer
      hide-header
      header-class="airspirit-modal-header"
      body-class="newsletter-subscriber-modal-body pl-4 pr-4"
      size="lg"
    >

      <div
        v-if="isVideoToPay"
        class="mt-4 mb-4"
      >
        <strong>{{ $t('learn_page.pay_per_view_message', { price: videoChosen.price }) }}</strong>
      </div>

      <div v-if="isVideoToPay">
        <div class="form-field" id="card-field"></div>
        <div>
          <input
            class="registration-input mt-3"
            :class="{'is-invalid-input': $v.cardInfo.cardholder.$dirty && $v.cardInfo.cardholder.$error}"
            type="text"
            maxlength="255"
            :placeholder="$t('learn_page.name_on_card_placeholder')"
            v-model="cardInfo.cardholder"
            @input="$v.cardInfo.cardholder.$touch()"
          />
        </div>

        <div>
          <input
            class="registration-input mt-4"
            :class="{'is-invalid-input': $v.cardInfo.email.$dirty && $v.cardInfo.email.$error}"
            type="email"
            maxlength="255"
            :placeholder="$t('learn_page.email_address_placeholder')"
            v-model="cardInfo.email"
            @keyup.enter="pay"
            @input="$v.cardInfo.email.$touch()"
          />
        </div>
        <button
          class="profile-delete-button mt-4 w-100"
          :disabled="$v.cardInfo.$invalid"
          @click="pay"
        >
          {{ $t('learn_page.pay_video_button') }}
        </button>
      </div> 

      <div
        v-if="videoChosen && videoChosen.video_access_option === 'INCLUDED_IN_SUBSCRIPTION' && !privatePerson.subscription_active"
      >
        <p>
          <strong>
            {{ $t('learn_page.subscription_required_message', { price: subscriptionPrice }) }}
          </strong>
        </p>
        <button 
          class="profile-validation-button"
          @click="$router.push('/private-person/account/subscription')"
        >
          {{ $t('learn_page.subscribe_button') }}
        </button>
      </div>
  
      <div 
        style="width: 100%; height: 700px;"
        v-else-if="videoChosen && !isVideoToPay"
      >
        <iframe 
          :src="`https://player.vimeo.com/video/${videoChosen['vimeo_id']}?badge=0&amp;autopause=0&amp;player_id=0&amp;app_id=58479`" 
          frameborder="0" 
          allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media" 
          style="position:absolute;top:0;left:0;width:100%;height:100%;" 
        />
      </div>
    </b-modal>

    <div class="annonces-menu" v-if="!isMobile()">
      <div class="video-categories mt-4">

        <div
          @click="clickOnLike"
          class="text-center categories-menu-tag-button"
        >
          <img
            class="categories-menu-tag-button-img"  
            src="@/assets/images/video/like_icon_black.svg"
            :alt="$t('learn_page.liked_only_alt')"
            width="36px"
          />
          <div class="categories-menu-tag-button-text">{{ $t('learn_page.liked_only_label') }}</div>
          <div 
            class="categories-menu-tag-button-border"
            :style="onlyLiked ? 'border-bottom: solid 2px black;' : ''"
          ></div>
        </div>
        <div
          v-for="category in categories"
          :key="`category-${category}`"
          class="video-category"
          @click="toggleCategory(category)"
        >
          <div 
            class="categories-menu-tag-button"
            
          >
            <img 
              class="categories-menu-tag-button-img"
              :src="getCategoryLogoUrl(category)"
              :alt="categoryLabel(category)"
            > 
            <div class="categories-menu-tag-button-text">{{ categoryLabel(category) }}</div>
            <div 
              class="categories-menu-tag-button-border"
              :style="categoryStyle(category)"
            ></div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <!-- Videos -->
      <div 
        v-for="(video, index) in videos"
        :key="`video-${index}`"
        class="col-12 col-md-4 p-2 mt-4"
      > 
        <ContentItem
          v-if="video.transcode_status === 'complete'"
          :content="video"
          :userId="connectedUserId"
          @click="clickOnVideo(video)"
          @click-pseudo="clickOnPseudo"
          @click-category="clickOnCategory"
          @like="likeVideo"
        />

        <div
          v-else-if="video.transcode_status === 'in_progress'"
        >
          <strong> {{ $t('learn_page.video_upload_in_progress') }} </strong>
        </div>
      </div>

      <!-- Aircraft Accidents -->
      <div 
        v-for="(accident, index) in accidents"
        :key="`accident-${index}`"
        class="col-12 col-md-4 p-2 mt-4"
      > 
        <ContentItem
          :content="accident"
          :userId="connectedUserId"
          @click="clickOnAccident(accident)"
          @like="likeVideo"
        />
      </div>

      <!-- Add your video button -->
      <div 
        class="col-12 col-md-4 p-2 mt-4"
      > 
        <div
          class="picture-img-cover-container"
        >
          <img
            @click="addYourVideo"
            src="@/assets/images/video/add_video_icon.svg"
            :alt="$t('learn_page.sell_your_video_alt')"
            style="width: 100%; aspect-ratio: 16/9; cursor: pointer;"
          />
        </div>
      </div>
    </div>

    <div 
      v-if="hasMore" 
      class="text-center has-more-videos-button mt-4">
      <button
        class="profile-validation-button"
        @click="nextVideos"
      >
        {{ $t('learn_page.more_videos_button') }}
      </button>
    </div>
  </div>
</template>

<script src="https://player.vimeo.com/api/player.js"></script>

<script>
import { APIContent } from '@/api/APIContent'
import { APIPrivatePersonConnected } from '@/api/APIPrivatePersonConnected'
import { APIUserConnected } from '@/api/APIUserConnected'

import ContentItem from '@/components/video/ContentItem'
import ContentModal from '@/components/video/ContentModal'
import { mobileDetectionMixins } from '@/mixins/mobileDetection'
import RevolutCheckout from '@revolut/checkout'
import { nextTick } from 'vue'

import { validationMixin } from 'vuelidate'
import { required, email } from 'vuelidate/lib/validators'
import { slugifyAssetName } from '@/utils/slugify'

const apiContent = new APIContent()
const apiPrivatePersonConnected = new APIPrivatePersonConnected()
const apiUserConnected = new APIUserConnected()


export default {
  name: 'LearnPage',
  components: {
    ContentItem,
    ContentModal
  },
  mixins: [mobileDetectionMixins, validationMixin],
  data() {
    return {
      accidents: [],
      categories: [
        'Recreational Pilot',
        'Professional Pilot',
        'Commercial Crew',
        'Aircraft Mechanic',
        'Drone Operator',
        'Engineering',
        'Traffic Controller',
      ],
      categoriesChosen: [],
      cardInfo: {
        email: null,
        cardholder: null,
      },
      privatePerson: {
        subscription_active: false,
      },
      contentChosen: null,
      isContentVideoModal: false,
      hasMore: false,
      isAccidentModal: false,
      isVideoModal: false,
      isVideoToPay : false,
      onlyLiked: false,
      page: 1,
      userId: null,
      specificVideoId: null,
      videos: [],
      videoChosen: null
    }
  },
  computed: {
    currency () {
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (timeZone.startsWith("America/")) {
        return "$"
      } else if (timeZone.startsWith("Europe/")) { 
        // Middle East souvent sous Asia/ ou Europe/
        return "€"
      } else {
        return "$"
      }
    },
    subscriptionPrice () {
      return this.currency === '€' ? '10€' : '$10'
    },
    isAuthenticated () {
      return this.$store.getters.isAuthenticated
    },
    searchText () {
      return this.$store.state.searchText
    },
    token () {
      return this.$store.state.auth.token
    },
    connectedUserId () {
      return this.$store.state.user.userPk
    }
  },
  async mounted () {
    if ('search' in this.$route.query) {
      this.$eventHub.$emit('setSearch', this.$route.query.search)
    }

    this.$eventHub.$on('search', () => {
      this.search()
    })
    
    if ('categories' in this.$route.query && this.$route.query.categories) {
      this.categoriesChosen = this.$route.query.categories.split(',')
    }

    if ('userId' in this.$route.query && this.$route.query.userId) {
      this.userId = Number(this.$route.query.userId)
    }

    if ('specificVideoId' in this.$route.query && this.$route.query.specificVideoId) {
      this.specificVideoId = Number(this.$route.query.specificVideoId)
    }

    if (this.isAuthenticated) {
      await this.getCurrentPrivatePerson()
    }
    this.getContents()
  },
  async created() {
    this.$store.commit('loadFromAPI')
    await this.getContents()
    this.$store.commit('endLoadingFromAPI')
  },
   validations: {
    cardInfo: {
      email: {
        email,
        required
      },
      cardholder: {
        required,
      },
    }
  },
  methods: {
    addYourVideo () {
      if (this.isAuthenticated) {
        this.$router.push({path: '/user/account/content'})
      } else {
        this.$store.commit('openLoginModal')
      }
    },
    categoryStyle(category) {
      return this.categoriesChosen.includes(category) ? `border-bottom: solid 2px black; padding-bottom: 5px;` : ''
    },
    categoryLabel(category) {
      const key = 'learn_page.category_' + slugifyAssetName(category).replace(/-/g, '_')
      return this.$t(key)
    },
    clickOnLike () {
      if (this.isAuthenticated) {
        this.onlyLiked = !this.onlyLiked
        this.getContents()
      } else {
        this.$store.commit('set', ['loginModalNotRedirect', true])
        this.$store.commit('openLoginModal')
      }
    },
    clickOnCategory (category) {
      this.categoriesChosen = []
      const index = this.categoriesChosen.indexOf(category)
      if (index > -1) {
        this.categoriesChosen.splice(index, 1)
      } else {
        this.categoriesChosen.push(category)
      }
      this.filterVideos()
    },
    clickOnPseudo (user) {
      const data = {
        "userId": user.id
      }
      const routeData = this.$router.resolve({ path: '/learn', query: data})
      window.open(routeData.href, '_blank');
    },

    clickOnAccident(accident) {
      // Use ContentModal for accidents
      this.contentChosen = accident
      this.isAccidentModal = true
    },

    async clickOnVideo(video) {
      if (!this.isAuthenticated) {
        this.$store.commit('openLoginModal')
        return
      }
      
      this.videoChosen = video
      
      // Check if video requires payment
      if (video.video_access_option === 'PAY_PER_VIEW' && !video.users_paid.includes(Number(this.connectedUserId))) {
        this.isVideoToPay = true
        this.isVideoModal = true
        await this.createOrder(video)
      }
      // Check if subscription is required
      else if (video.video_access_option === 'INCLUDED_IN_SUBSCRIPTION' && !this.privatePerson.subscription_active) {
        this.isVideoModal = true
      }
      // Video is free or user has access - use ContentModal
      else {
        this.contentChosen = video
        this.isContentVideoModal = true
      }
    },

    async createOrder(video) {
      const data = {
        amount: video.price * 100,
        currency: 'USD',
      }
      const response = await apiUserConnected.createOrder(data) // Await the API response

      const orderToken = response.data['token']
      //const env = process.env.NODE_ENV === 'production' ? 'prod' : 'sandbox'
      const env = 'sandbox'
      const { createCardField } = await RevolutCheckout(orderToken, env) // Await RevolutCheckout
      
      // Wait for vue nextTick
      await nextTick()

      // Initialize the card field
      this.cardField = createCardField({
        target: document.getElementById("card-field"),
        hidePostcodeField: true,
        styles: {
          default: {
            color: "black",
            "::placeholder": {
              color: "#888888",      /* Gray color for placeholder */
              "font-size": "16px",     /* Adjust font size */
              opacity: 0.4,          /* Full opacity */
              "font-weight": 100,
              "font-family": "Arial"
            }
          },
          autofilled: {
            color: "#fff"
          }
        },
        onSuccess: async () => {
          // Handle successful payments
          const response = await apiContent.confirmPayContent(this.videoChosen.id)
          const content = response.data
          // Update content in this.videos
          this.videos = this.videos.map((item) => {
            if (item.id === content.id) {
              return content
            }
            return item
          })
          this.isVideoToPay = false
          this.isVideoModal = false
          // Now open the video in ContentModal
          this.contentChosen = content
          this.isContentVideoModal = true
        },
        onError: (error) => {
          // Handle payment errors
          window.alert(this.$t('learn_page.payment_error_message', { error: error }))
        }
      })
    },

    displayImageWithObjectUrl(file) {
      if (typeof file === 'string') {
        return file
      }
      return URL.createObjectURL(file)
    },

    filterVideos () {
      const data = {
        'categories': this.categoriesChosen,
      }
      this.$router.push({path: "/learn", query: data})
      this.getContents()
    },
    getCategoryLogoUrl(category) {
      return require('@/assets/images/video/logo_' + slugifyAssetName(category) + '.svg')
    },
    async getCurrentPrivatePerson () {
      return apiPrivatePersonConnected.getCurrentPrivatePerson(this.token)
      .then((response) => {
        this.privatePerson = response.data
      }, (error) => {
        throw new Error('getCurrentPrivatePerson API Error : ' + String(error))
      })
    },
    getContents (isNext=false) {
      this.$store.commit('loadFromAPI')
      // Fetch both VIDEO and AIRCRAFT_ACCIDENT types, or filter by contentType if needed
      apiContent.getContents(
        this.page, 
        null, // contentType - get all types
        this.categoriesChosen, 
        this.searchText, 
        this.userId, 
        this.specificVideoId,
        null, // withNotPublished
        this.onlyLiked ? true : null
      )
      .then((result) => {
        this.hasMore = result.data['hasMore']
        const contents = result.data['contents']
        
        // Separate videos and accidents
        const newVideos = contents.filter(c => c.content_type === 'VIDEO')
        const newAccidents = contents.filter(c => c.content_type === 'AIRCRAFT_ACCIDENT')
        
        if (isNext) {
          this.videos = this.videos.concat(newVideos)
          this.accidents = this.accidents.concat(newAccidents)
        } else {
          this.videos = newVideos
          this.accidents = newAccidents
        }
      })
      .catch((error) => {
        throw new Error('getContents API Error : ' + String(error))
      })
      .finally(() => {
        this.$store.commit('endLoadingFromAPI')
      })
    },
    getVideos (isNext=false) {
      // Now just calls getContents
      this.getContents(isNext)
    },
    getAccidents (isNext=false) {
      // Accidents are now fetched together with videos via getContents
      // This method can remain empty or be removed after full migration
    },
    getVideosLiked (isNext=false) {
      // Now handled by getContents with onlyLiked parameter
      this.getContents(isNext)
    },
    likeImage (content) {
      return content.likes.includes(Number(this.connectedUserId))
        ? require('@/assets/images/video/like_icon_full.svg')
        : require('@/assets/images/video/like_icon_black.svg')
    },
    likeVideo (content) {
      // if not connected
      if (!this.isAuthenticated) {
        this.$store.commit('set', ['loginModalNotRedirect', true])
        this.$store.commit('openLoginModal')
        return
      }
      this.$store.commit('loadFromAPI')
      apiContent.likeContent(content.id)
      .then(() => {
        // Update both videos and accidents arrays
        this.videos = this.videos.map((item) => {
          if (item.id === content.id) {
            if (item.likes.includes(Number(this.connectedUserId))) {
              item.likes = item.likes.filter((item) => item !== Number(this.connectedUserId))
            } else {
              item.likes.push(Number(this.connectedUserId))
            }
          }
          return item
        })
        this.accidents = this.accidents.map((item) => {
          if (item.id === content.id) {
            if (item.likes.includes(Number(this.connectedUserId))) {
              item.likes = item.likes.filter((item) => item !== Number(this.connectedUserId))
            } else {
              item.likes.push(Number(this.connectedUserId))
            }
          }
          return item
        })
      })
      .catch((error) => {
        throw new Error('likeContent API Error : ' + String(error))
      })
      .finally(() => {
        this.$store.commit('endLoadingFromAPI')
      })
    },
    nextVideos () {
      this.page += 1
      this.getContents(true)
    },
    pay () {
      const data = {
        name: this.cardInfo.cardholder,
        email: this.cardInfo.email
      }
      this.cardField.submit(data)
    },
    search () {
      this.categoriesChosen = []
      const data  = {
        'categories': null,
        'search': this.searchText
      }
      this.$router.push({path: "/learn", query: data})
      this.getContents()
    },
    toggleCategory(category) {
      const index = this.categoriesChosen.indexOf(category)
      if (index === -1) {
        //this.categoriesChosen.push(category.id)
        // only one category authorized
        this.categoriesChosen = [category]
      } else {
        //this.categoriesChosen.splice(index, 1)
        // only one category authorized
        this.categoriesChosen = []
      }
      this.filterVideos()
      this.searchText = ''
      this.$refs.basicNavbar.searchText = ''
    },
  },
}
</script>

<style>
.categories-menu {
  background-color: white;
  padding-right: 45px;
  padding-left: 30px;
  padding-top: 10px;
  position: relative;
  width: 100%;
  z-index: 999;
}

@media (min-width: 800px) {
  .categories-menu {
    padding-right: 100px;
    padding-left: 100px;
    margin-top: 10px;
    padding-top: 0px;
  }
}
.categories-menu-tag-buttons {
  display: flex;
  flex-flow: row wrap;
  margin-bottom: 15px;
}

.categories-menu-tag-button {
  cursor: pointer;
  position: relative;
  height: 60px;
}

@media (min-width: 800px) {
  .categories-menu-tag-button {
    height: 100px;
  }
}

.categories-menu-tag-button-img {
  display: unset !important;
  height: 25px;
  height: 25px;
}

@media (min-width: 800px) {
  .categories-menu-tag-button-img {
    height: 40px;
    height: 40px;
  }
}

.categories-menu-tag-button-text {
  font-size: 8px;
  font-weight: bold;
  margin-top: 15px;
  text-transform: uppercase;
}

@media (min-width: 800px) {
  .categories-menu-tag-button-text  {
    font-size: 10px;
  }
}

.categories-menu-tag-button-border {
  position: absolute;
  left: 25%;
  right:25%;
  margin-top: 5px;
}

@media (min-width: 800px) {
  .categories-menu-tag-button-border {
    margin-top: 10px;
  }
}

.categories-menu-vue-slick-carousel--arrow-right {
  bottom: 50%; 
  height: 15px;
  opacity: 0.3;
  position: absolute;
  right: 30px;
}

.categories-menu .slick-prev {
  display: none !important;
}

.categories-menu .slick-next {
  margin-top: -10px;
}
.categories-menu .slick-prev:before, .categories-menu .slick-next:before {
  opacity: 0.4 !important;
}

.categories-menu .slick-next:before {
  font-size: 30px;
}

</style>